import { supabase } from '../lib/supabaseClient';

const isDev = import.meta.env.DEV;

const debugLog = (...args: unknown[]) => {
  if (isDev) {
    console.log(...args);
  }
};

const debugWarn = (...args: unknown[]) => {
  if (isDev) {
    console.warn(...args);
  }
};

interface SessionInfo {
  userId: string;
  status: 'online' | 'away' | 'offline';
  lastActivity: Date;
  device?: string;
}

class SessionManager {
  private updateInterval: NodeJS.Timeout | null = null;
  private currentUserId: string | null = null;

  async createSession(userId: string): Promise<boolean> {
    debugLog('sessionManager.createSession', { userId, type: typeof userId });

    if (!userId) {
      debugWarn('sessionManager.createSession missing userId');
      return false;
    }

    try {
      this.currentUserId = userId;

      const deviceInfo = {
        device: navigator.userAgent.includes('Mobile') ? 'Mobile' : 'Desktop',
        browser: this.getBrowserName(),
        os: this.getOSName(),
      };

      debugLog('sessionManager.deviceInfo', deviceInfo);

      const { data, error } = await supabase
        .from('user_sessions')
        .insert({
          user_id: userId,
          session_id: `${userId}-${Date.now()}`,
          status: 'online',
          device_info: deviceInfo,
          user_agent: navigator.userAgent,
        })
        .select();

      debugLog('sessionManager.insertResult', { data, error });

      if (error) {
        console.error('Oturum oluşturma hatası:', {
          message: error.message,
          code: error.code,
          details: error.details,
          hint: error.hint,
        });
        return false;
      }

      if (!data || data.length === 0) {
        console.error('Oturum oluşturuldu ancak kayıt bilgisi dönmedi');
        return false;
      }

      debugLog('sessionManager.createSession success', {
        userId,
        sessionId: data[0]?.session_id,
        createdAt: data[0]?.created_at,
      });

      this.startActivityUpdates(userId);
      return true;
    } catch (err) {
      console.error('Oturum oluşturma exception:', err);
      return false;
    }
  }

  private startActivityUpdates(userId: string): void {
    if (this.updateInterval) clearInterval(this.updateInterval);

    this.updateActivity(userId);

    this.updateInterval = setInterval(() => {
      this.updateActivity(userId);
    }, 30000);
  }

  private async updateActivity(userId: string): Promise<void> {
    try {
      debugLog('sessionManager.updateActivity', userId);

      const { data: sessions, error: fetchError } = await supabase
        .from('user_sessions')
        .select('id')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(1);

      if (fetchError || !sessions || sessions.length === 0) {
        debugWarn('sessionManager.noSessionFound', userId);
        return;
      }

      const sessionId = sessions[0].id;

      const { data, error } = await supabase
        .from('user_sessions')
        .update({
          last_activity: new Date().toISOString(),
          status: 'online',
        })
        .eq('id', sessionId)
        .select();

      if (error) {
        console.error('Aktivite güncelleme hatası:', {
          message: error.message,
          code: error.code,
        });
        return;
      }

      if (data && data.length > 0) {
        debugLog('sessionManager.activityUpdated', {
          userId,
          lastActivity: data[0]?.last_activity,
        });
      }
    } catch (err) {
      console.error('Aktivite güncelleme exception:', err);
    }
  }

  async endSession(userId: string): Promise<void> {
    if (this.updateInterval) {
      clearInterval(this.updateInterval);
      this.updateInterval = null;
    }

    try {
      const { data: sessions, error: fetchError } = await supabase
        .from('user_sessions')
        .select('id')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(1);

      if (fetchError || !sessions || sessions.length === 0) {
        debugWarn('sessionManager.noSessionFoundOnEnd', userId);
        return;
      }

      const sessionId = sessions[0].id;

      await supabase
        .from('user_sessions')
        .update({
          status: 'offline',
          last_activity: new Date().toISOString(),
        })
        .eq('id', sessionId);

      debugLog('sessionManager.sessionEnded', userId);
    } catch (err) {
      console.error('Oturum kapatma hatası:', err);
    }
  }

  private getBrowserName(): string {
    const ua = navigator.userAgent;
    if (ua.includes('Chrome')) return 'Chrome';
    if (ua.includes('Safari')) return 'Safari';
    if (ua.includes('Firefox')) return 'Firefox';
    if (ua.includes('Edge')) return 'Edge';
    return 'Unknown';
  }

  private getOSName(): string {
    const ua = navigator.userAgent;
    if (ua.includes('Windows')) return 'Windows';
    if (ua.includes('Mac')) return 'macOS';
    if (ua.includes('Linux')) return 'Linux';
    if (ua.includes('Android')) return 'Android';
    if (ua.includes('iPhone') || ua.includes('iPad')) return 'iOS';
    return 'Unknown';
  }

  cleanup(): void {
    if (this.updateInterval) {
      clearInterval(this.updateInterval);
      this.updateInterval = null;
    }
    this.currentUserId = null;
  }
}

export const sessionManager = new SessionManager();
